// The last place a reader looked at, and when it is at its best for them.
//
// Written wherever a city's year is on screen — the app's briefing and a
// guide's page — and read by the landing, whose sentence ("I loved Sydney in
// mid April, around midday") otherwise opens on a guess. A place the reader
// chose to look at is a better first word than one picked for them.
//
// The time is the place's best ten-day section for the reader's own rating,
// "My kind of day", when they have made one; for being outside when they have
// not. Its own small key beside the app's, because a guide writes it too and a
// guide knows nothing else of the app's state.

import type { CitySeries } from '../contracts/city.ts';

const KEY = 'fcc.last.v1';

export interface LastPlace {
  id: number;
  name: string;
  country: string;
  placeSlug?: string;
  /** Section key, "MM-p". */
  dekad: string;
  bucket: string;
  /** Whether the time is the reader's own rating's peak, or the stand-in's. */
  mine: boolean;
  /** When it was looked at, ms. */
  at: number;
}

// The middle of the longest run of sections at (or within a point of) the best
// score, wrapping the year end — a plateau from May to July peaks in June, not
// in May. The same rule lib/peaks.ts applies on the server, which this cannot
// import: that module reads the match table and is not browser code.
function peakDekad(scores: readonly (number | null)[], best: number): number {
  const n = scores.length;
  const at = (i: number) => { const v = scores[((i % n) + n) % n]; return v != null && v >= best - 1; };
  let start = -1, len = 0;
  for (let i = 0; i < n; i++) {
    if (!at(i) || at(i - 1)) continue;
    let l = 1;
    while (l < n && at(i + l)) l++;
    if (l > len) { len = l; start = i; }
  }
  if (start < 0) return scores.findIndex((v) => v != null && v >= best - 1);
  return (start + Math.floor((len - 1) / 2)) % n;
}

export function rememberPlace(s: CitySeries, dates: readonly string[], opts: { custom: boolean; bucket: string }): void {
  const mine = opts.custom && !!s.series.custom && s.series.custom.some((v) => v != null && v > 0);
  const scores = (mine ? s.series.custom : s.series.relax) || [];
  let best = -Infinity;
  for (const v of scores) if (v != null && v > best) best = v;
  if (!Number.isFinite(best)) return;
  const d = peakDekad(scores, best);
  if (d < 0 || !dates[d]) return;
  const entry: LastPlace = { id: s.id, name: s.name, country: s.country, placeSlug: s.placeSlug, dekad: dates[d], bucket: opts.bucket, mine, at: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* best-effort, like the rest of the persistence */
  }
}

export function lastPlace(): LastPlace | null {
  try {
    const x = JSON.parse(localStorage.getItem(KEY) || 'null') as LastPlace | null;
    if (!x || !Number.isInteger(x.id) || typeof x.name !== 'string' || typeof x.country !== 'string') return null;
    if (typeof x.dekad !== 'string' || !/^\d{2}-[123]$/.test(x.dekad) || typeof x.bucket !== 'string') return null;
    return x;
  } catch {
    return null;
  }
}
